import React, { useId, useState, useEffect, useCallback } from 'react';
import { classnames } from 'Utils/classnames/classnames.utils';
import { noop } from 'lodash';
import { cleanupId } from 'Utils/unique-id/uniqueId';
import { ICONS } from 'Components/utils/icon/icons';
import { Icon } from 'Components/frameworks/icon/Icon';
import { themes } from './InputNumber.themes';
type Props = {
  ariaLabel?: string;
  autoComplete?: AutoComplete;
  className?: string;
  dataTestId?: string;
  hasColumnLayout?: boolean;
  id?: string;
  isDisabled?: boolean;
  isLoading?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  label?: string | Node;
  max?: number;
  min?: number;
  name?: string;
  notes?: string;
  onBlur?: (arg0: React.SyntheticEvent) => void;
  onChange?: (name: string, value: number) => void;
  onClick?: (arg0: React.SyntheticEvent) => void;
  onFocus?: (arg0: React.SyntheticEvent) => void;
  onKeyDown?: (arg0: React.KeyboardEvent) => void;
  onKeyUp?: (arg0: React.KeyboardEvent) => void;
  placeholder?: number;
  step?: number;
  theme?: keyof typeof themes;
  validationState?: string;
  value?: number | null | undefined;
};
const defaultPropsValues = {
  autoComplete: 'off' as AutoComplete,
  max: 10,
  min: 0,
  step: 1,
  theme: 'default' as keyof typeof themes,
  validationState: 'NOT_VALIDATED',
};
export function InputNumber(rawProps: Props) {
  const props = { ...defaultPropsValues, ...rawProps };
  const {
    ariaLabel,
    autoComplete,
    className,
    dataTestId = 'InputNumber',
    hasColumnLayout = false,
    isDisabled,
    isLoading,
    isReadOnly,
    isRequired,
    label,
    max,
    min,
    name,
    notes,
    onBlur,
    onChange = noop,
    onClick,
    onFocus,
    onKeyDown,
    onKeyUp,
    placeholder,
    step,
    theme,
    validationState,
  } = props;

  const reactId = useId();
  const labelStr = typeof label === 'string' ? cleanupId(label) : '';
  const id = props.id ?? (labelStr ? `InputNumber_${labelStr}` : `InputNumber_${reactId}`);

  const [value, setValue] = useState<number | null | undefined>(props.value);

  // This component renders client-side only. We must return the same output
  // (null) on the server and on the first client render so that hydration
  // matches, then reveal the real markup after mount. Guarding directly on
  // `typeof window` instead would make the first client render differ from the
  // server HTML and break hydration.
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (props.value !== value) {
      setValue(props.value);
    }
  }, [props.value]);

  const handleChange = useCallback(
    (e: React.SyntheticEvent) => {
      if (!isReadOnly && !isLoading) {
        // @ts-expect-error TODO(flow-migration)
        const newValue = Number(e.target.value);
        setValue(newValue);
        onChange(name ?? '', newValue);
      }
    },
    [isReadOnly, isLoading, name, onChange],
  );

  const decrease = useCallback(() => {
    const currentValue = value || min;
    const decreasedValue = currentValue - step;
    const nextValue = decreasedValue >= min ? decreasedValue : min;
    setValue(nextValue);
    onChange(name ?? '', nextValue);
  }, [value, min, step, name, onChange]);

  const increase = useCallback(() => {
    const currentValue = value || min;
    const increasedValue = currentValue + step;
    const nextValue = increasedValue <= max ? increasedValue : max;
    setValue(nextValue);
    onChange(name ?? '', nextValue);
  }, [value, min, max, step, name, onChange]);

  const hasValue = Boolean(value);
  const decreaseDisabled = isDisabled || isLoading || Number(value) <= min;
  const increaseDisabled = isDisabled || isLoading || Number(value) >= max;
  const { thButton, thButtonIconWidth, thInput, thInputs, thLabel, thNotes, thRoot } = themes[
    theme
  ]({
    hasColumnLayout,
    hasValue,
    isDisabled,
    isReadOnly,
    validationState,
  });

  if (!isMounted) {
    return null;
  }

  return (
    <div className={classnames(thRoot, className)} data-testid={dataTestId}>
      {!!label && (
        <label className={thLabel} htmlFor={id}>
          {label}
          {!!notes && <span className={thNotes}>{notes}</span>}
        </label>
      )}
      <div className={thInputs}>
        {!isReadOnly && (
          <button
            className={thButton}
            data-testid={`${dataTestId}-decrease`}
            disabled={decreaseDisabled}
            onClick={decrease}
            type="button"
          >
            <Icon name={ICONS.MINUS} width={thButtonIconWidth} />
          </button>
        )}
        <input
          aria-label={ariaLabel}
          autoComplete={autoComplete}
          className={thInput}
          disabled={isDisabled}
          id={id}
          max={max}
          min={min}
          {...(name
            ? {
                name,
              }
            : null)}
          onBlur={onBlur}
          onChange={handleChange}
          onClick={onClick}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onKeyUp={onKeyUp}
          {...(placeholder
            ? {
                placeholder: placeholder,
              }
            : null)}
          readOnly
          required={isRequired}
          step={step}
          style={{
            width: `${max.toString().length + 0.5}em`,
          }}
          type="number"
          value={value}
        />
        {!isReadOnly && (
          <button
            className={thButton}
            data-testid={`${dataTestId}-increase`}
            disabled={increaseDisabled}
            onClick={increase}
            type="button"
          >
            <Icon name={ICONS.PLUS} width={thButtonIconWidth} />
          </button>
        )}
      </div>
    </div>
  );
}
